import React from 'react';
import { FaRegUserCircle } from 'react-icons/fa';
import ReactMarkdown from 'react-markdown';
import { ChatMessage, ModelId } from '../../types';
import { CopyButton } from '../buttons/CopyButton';
import { InsertButton } from '../buttons/InsertButton';
import { Card, CardContent, CardFooter, CardHeader } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import { cn } from '@/utils';

interface ChatBubbleProps {
  message: ChatMessage;
  isStreaming: boolean;
  item: Office.MessageRead | Office.MessageCompose | null;
  isDisabled?: boolean;
  setIsInserting: (isInserting: boolean) => void;
  model: ModelId;
  /** Standalone browser mode (no Office host): hide the Insert button (nothing to insert into). */
  standalone?: boolean;
}

export const ChatBubble: React.FC<ChatBubbleProps> = ({
  message,
  isStreaming,
  item,
  isDisabled = false,
  setIsInserting,
  model,
  standalone = false
}) => {
  const isUser = message.role === "user";

  return (
    <Card className={cn(
      "mb-2 w-full max-w-full",
      isUser ? "bg-blue-50 border-blue-200" : "bg-gray-50 border-gray-200"
    )}>
      <CardHeader className="p-2 pb-1">
        <div className="flex items-center gap-2">
          {isUser ? (
            <Avatar className="h-6 w-6">
              <AvatarFallback>
                <FaRegUserCircle className="h-4 w-4" />
              </AvatarFallback>
            </Avatar>
          ) : (
            <Avatar className="h-6 w-6">
              <AvatarImage src="assets/ep.png" alt="AI" />
              <AvatarFallback>AI</AvatarFallback>
            </Avatar>
          )}
          <div className="font-semibold text-sm">
            {isUser ? "You" : "My AI Secretary"}
          </div>
        </div>
      </CardHeader>

      <CardContent className="p-2 pt-1">
        <div className="flex">
          <ScrollArea type="always" className="w-1 flex-1">
            <div className="prose prose-sm max-w-none break-words text-sm [&_*]:text-foreground">
              <ReactMarkdown
                components={{
                  p: ({ children }) => <p className="mb-2 last:mb-0 leading-relaxed">{children}</p>,
                  h1: ({ children }) => <h1 className="text-lg font-bold mb-2 mt-3 first:mt-0">{children}</h1>,
                  h2: ({ children }) => <h2 className="text-base font-bold mb-2 mt-3 first:mt-0">{children}</h2>,
                  h3: ({ children }) => <h3 className="text-sm font-semibold mb-1 mt-2 first:mt-0">{children}</h3>,
                  ul: ({ children }) => <ul className="list-disc list-inside mb-2 space-y-1 ml-2">{children}</ul>,
                  ol: ({ children }) => <ol className="list-decimal list-inside mb-2 space-y-1 ml-2">{children}</ol>,
                  li: ({ children }) => <li className="ml-1">{children}</li>,
                  code: (props: any) => {
                    const { inline, className, children, ...rest } = props;
                    const match = /language-(\w+)/.exec(className || '');
                    return !inline ? (
                      <pre className="bg-muted p-2 rounded text-xs font-mono overflow-x-auto mb-2">
                        <code className={className} {...rest}>
                          {children}
                        </code>
                      </pre>
                    ) : (
                      <code className="bg-muted px-1.5 py-0.5 rounded text-xs font-mono" {...rest}>
                        {children}
                      </code>
                    );
                  },
                  blockquote: ({ children }) => (
                    <blockquote className="border-l-4 border-muted-foreground/30 pl-3 italic mb-2 text-muted-foreground">
                      {children}
                    </blockquote>
                  ),
                  a: ({ href, children }) => (
                    <a 
                      href={href} 
                      className="text-primary underline hover:text-primary/80" 
                      target="_blank" 
                      rel="noopener noreferrer"
                    >
                      {children}
                    </a>
                  ),
                  strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
                  em: ({ children }) => <em className="italic">{children}</em>,
                  hr: () => <hr className="my-3 border-t border-border" />,
                  table: ({ children }) => (
                    <div className="mb-2 whitespace-nowrap">
                      <table className="min-w-full border-collapse border border-border">
                        {children}
                      </table>
                    </div>
                  ),
                  th: ({ children }) => (
                    <th className="border border-border px-2 py-1 bg-muted font-semibold text-left whitespace-nowrap">
                      {children}
                    </th>
                  ),
                  td: ({ children }) => (
                    <td className="border border-border px-2 py-1 whitespace-nowrap">
                      {children}
                    </td>
                  ),
                }}
              >
                {message.content}
              </ReactMarkdown>
            </div>
            <ScrollBar orientation="horizontal" className="w-full" />
          </ScrollArea>
        </div>
      </CardContent>

      {/* Show footer buttons only for AI messages */}
      {!isUser && (
        <CardFooter className="p-2 pt-1 gap-1">
          {/* Insert targets the active Office document/email; not available standalone. */}
          {!standalone && (
            <InsertButton
              mailContent={message.content}
              item={item}
              disabled={isStreaming || isDisabled}
              setIsInserting={setIsInserting}
              model={model}
            />
          )}
          <CopyButton
            textToCopy={message.content} 
            disabled={isStreaming || isDisabled}
          />
        </CardFooter>
      )}
    </Card>
  );
};